import { useEffect, useState } from "react";
import { Layers, Loader2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, getRef, getReward } from "../../lib/api";
import Countdown from "./Countdown";

const FlagshipBundle = ({ bundle, onBuy, buyingId }) => (
  <div className="relative overflow-hidden border border-emerald-500/25 bg-slate-950">
    <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-[100px]" />
    <div className="relative grid grid-cols-1 gap-8 p-8 lg:grid-cols-2 lg:p-12">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">
            <Layers className="h-4 w-4" /> Best value · Bundle
          </span>
          {bundle.sale_ends_at && (
            <span className="flex items-center gap-1.5 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">Ends in</span>
              <Countdown endsAt={bundle.sale_ends_at} testid={`bundle-countdown-${bundle.id}`} />
            </span>
          )}
        </div>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tighter text-white lg:text-4xl">{bundle.name}</h2>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{bundle.description}</p>
        <div className="mt-6 flex flex-wrap items-end gap-4">
          <span className="font-mono text-4xl font-bold text-emerald-400" data-testid="bundle-price">${bundle.bundle_price}</span>
          <span className="font-mono text-lg text-slate-500 line-through">${bundle.original_price}</span>
          <span className="border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 font-mono text-xs uppercase tracking-wider text-emerald-400">
            Save ${bundle.savings} · {bundle.discount_pct}% off
          </span>
        </div>
        <button
          data-testid="bundle-buy-btn"
          onClick={() => onBuy(bundle)}
          disabled={buyingId === bundle.id}
          className="mt-7 inline-flex items-center gap-2 bg-emerald-500 px-7 py-3.5 font-semibold text-slate-900 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
        >
          {buyingId === bundle.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Get the {bundle.count}-template stack
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {bundle.products.map((p) => (
          <div key={p.id} data-testid={`bundle-item-${p.id}`} className="flex items-center gap-3 border border-white/10 bg-slate-900 p-3">
            <img src={p.image_url} alt={p.title} className="h-11 w-11 flex-shrink-0 object-cover" />
            <div className="min-w-0">
              <div className="flex items-center gap-1 truncate text-sm font-semibold text-white">
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" /> {p.title}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{p.software_type} · ${p.base_price}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MiniBundle = ({ bundle, onBuy, buyingId }) => (
  <div data-testid={`bundle-card-${bundle.id}`} className="flex flex-col border border-white/10 bg-slate-950 p-6">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
        <Layers className="h-3.5 w-3.5 text-emerald-400" /> {bundle.count} templates
      </span>
      {bundle.sale_ends_at && <Countdown endsAt={bundle.sale_ends_at} testid={`bundle-countdown-${bundle.id}`} />}
    </div>
    <h3 className="mt-3 font-display text-xl font-extrabold tracking-tight text-white">{bundle.name}</h3>
    <p className="mt-1.5 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-400">{bundle.description}</p>
    <div className="mt-4 flex items-end gap-2.5">
      <span className="font-mono text-2xl font-bold text-emerald-400" data-testid={`bundle-price-${bundle.id}`}>${bundle.bundle_price}</span>
      <span className="font-mono text-sm text-slate-500 line-through">${bundle.original_price}</span>
      <span className="mb-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-500">{bundle.discount_pct}% off</span>
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      {bundle.products.map((p) => (
        <span key={p.id} className="border border-white/10 px-2 py-0.5 font-mono text-[10px] text-slate-400">{p.title}</span>
      ))}
    </div>
    <button
      data-testid={`bundle-buy-btn-${bundle.id}`}
      onClick={() => onBuy(bundle)}
      disabled={buyingId === bundle.id}
      className="mt-5 inline-flex items-center justify-center gap-2 border border-emerald-500/50 px-5 py-2.5 text-sm font-semibold text-emerald-300 transition-colors duration-200 hover:bg-emerald-500 hover:text-slate-900 disabled:opacity-60"
    >
      {buyingId === bundle.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
      Get this bundle
    </button>
  </div>
);

export const BundleOffer = () => {
  const [bundles, setBundles] = useState([]);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    api.bundles().then((r) => setBundles(r.bundles || [])).catch((err) => console.error("Failed to load bundles", err?.message || err));
  }, []);

  const buy = async (bundle) => {
    setBuyingId(bundle.id);
    try {
      const r = await api.checkoutBundle(bundle.id, window.location.origin, getRef(), getReward());
      if (r.checkout_url) window.location.href = r.checkout_url;
      else toast.success(r.message || "Unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBuyingId(null);
    }
  };

  if (bundles.length === 0) return null;
  const [flagship, ...rest] = bundles;

  return (
    <section id="bundles" data-testid="bundle-offer" className="mx-auto max-w-[1400px] scroll-mt-24 px-6 py-14 lg:px-10">
      <FlagshipBundle bundle={flagship} onBuy={buy} buyingId={buyingId} />
      {rest.length > 0 && (
        <>
          <div className="mb-5 mt-12 flex items-center gap-3">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">More bundles</h3>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {rest.map((b) => (
              <MiniBundle key={b.id} bundle={b} onBuy={buy} buyingId={buyingId} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default BundleOffer;
